/*
 * <variant-slider> — progressive-enhancement wrapper that turns a row of radio
 * swatches for ONE ordered variant option (e.g. a gift card "Denomination" with
 * many values) into a single range slider.
 *
 * Ported from the boerboelwear theme. The mechanism is unchanged; only the
 * selectors differ, because this theme wraps each radio in `.swatch-element`
 * rather than using a bare `.swatch-lbl`.
 *
 * Why this design:
 * - The hidden radios stay the source of truth. This theme binds
 *   `$('.single-option-selector-<sectionId>').on('change', ...)` (theme.js:339)
 *   and resolves the variant from whichever radio is checked, so the slider only
 *   has to move the checked radio and re-dispatch a bubbling `change`. Nothing
 *   in the variant pipeline has to know the slider exists. jQuery binds through
 *   addEventListener, so a natively dispatched event does reach those handlers.
 * - No-JS / JS-error fallback: the radios and their labels render normally. The
 *   `slider-ready` class — which hides the swatches and reveals the track via
 *   CSS — is only added once this element has wired itself up, so a failure
 *   degrades visibly to working swatches rather than an inert control.
 * - The variant change (price, URL, media) fires on `change` only, i.e. when the
 *   handle is released or an arrow key is pressed — not on every `input` tick
 *   while dragging, which would spam the section-render fetch.
 */
if (!customElements.get('variant-slider')) {
  customElements.define(
    'variant-slider',
    class VariantSlider extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        // Live value readout in the row's header label. Optional — the slider
        // works without it.
        this.display = this.closest('.swatch')?.querySelector('[data-slider-value]');

        if (!this.range || this.radios.length === 0) {
          // Structural problem (template changed out from under us) — surface it
          // and leave the swatch fallback visible rather than hiding it.
          console.warn('[variant-slider] missing range input or radios; leaving swatch fallback.', this);
          return;
        }

        this.range.max = String(this.radios.length - 1);
        const checkedIndex = this.radios.findIndex((radio) => radio.checked);
        this.range.value = String(checkedIndex < 0 ? 0 : checkedIndex);

        this.range.addEventListener('input', () => this.select(false));
        this.range.addEventListener('change', () => this.select(true));

        this.classList.add('slider-ready');
        this.select(false);
      }

      // index -> check that radio, update the live label and filled-track width,
      // and (only when committing) re-dispatch change so the rest of the variant
      // pipeline runs.
      select(commit) {
        const index = Number(this.range.value);
        const radio = this.radios[index];
        if (!radio) return;

        this.radios.forEach((r) => {
          r.checked = false;
        });
        radio.checked = true;

        if (this.display) this.display.textContent = radio.value;

        const max = Number(this.range.max) || 1;
        this.style.setProperty('--slider-progress', `${(index / max) * 100}%`);

        if (commit) radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}
